import { useEffect, useState } from "react";

/**
 * Connect YouTube, per profile: the native sign-in (YoutubeAccountPlugin.java, build guide
 * docs/social-connect-youtube.md Phase 2b) as the page sees it.
 *
 * Google's sign-in for TVs: the TV asks for a short code, the viewer enters it at
 * google.com/device on a phone, and the TV polls until Google says yes. The refresh token never
 * reaches the page: it stays in the APK, encrypted. The page only ever holds a short-lived
 * access token, to call Google's API straight from the device.
 *
 * Only inside an APK that carries the plugin AND was built with the OAuth client (the play and
 * tenant builds are not). On the website, and in an older APK, every call answers "not
 * available" and nothing listens.
 */

export type ConnectState = "waiting" | "connected" | "denied" | "expired" | "error";

export interface DeviceCodeInfo {
  userCode: string;
  verificationUrl: string;
  /** Seconds the code works for. */
  expiresIn: number;
  /** Seconds between the TV's checks. */
  interval: number;
}

/** What went wrong, as a code the page can act on: "unavailable" | "offline" | "disconnected" | "error". */
export class YoutubeAccountError extends Error {
  constructor(readonly code: string, message?: string) {
    super(message ?? code);
    this.name = "YoutubeAccountError";
  }
}

interface CapacitorLike {
  isNativePlatform?: () => boolean;
  PluginHeaders?: Array<{ name: string }>;
  nativePromise?: (plugin: string, method: string, options: Record<string, unknown>) => Promise<unknown>;
  addListener?: (plugin: string, event: string, callback: (data: never) => void) => { remove: () => unknown };
}

const PLUGIN = "YoutubeAccount";

function capacitor(): CapacitorLike | null {
  const cap = (globalThis as { Capacitor?: CapacitorLike }).Capacitor;
  if (cap?.isNativePlatform?.() !== true || typeof cap.nativePromise !== "function") return null;
  if (!cap.PluginHeaders?.some((header) => header.name === PLUGIN)) return null;
  return cap;
}

async function call<T>(method: string, options: Record<string, unknown> = {}): Promise<T> {
  const cap = capacitor();
  if (!cap) throw new YoutubeAccountError("unavailable");
  try {
    return (await cap.nativePromise!(PLUGIN, method, options)) as T;
  } catch (error) {
    const code = (error as { code?: unknown } | null)?.code;
    throw new YoutubeAccountError(typeof code === "string" ? code : "error");
  }
}

// --- Is signing in possible here? ----------------------------------------------------------

let availability: Promise<boolean> | null = null;

/** True when this build can connect a YouTube account (the plugin is there and has its client). */
export function connectAvailable(): Promise<boolean> {
  if (!capacitor()) return Promise.resolve(false);
  availability ??= call<{ available: boolean }>("available")
    .then((answer) => answer.available === true)
    .catch(() => false);
  return availability;
}

/** Forgets what connectAvailable learned. Tests only. */
export function resetYoutubeAccountForTests(): void {
  availability = null;
  changeListeners.clear();
  disconnectListeners.clear();
}

// --- Connecting ----------------------------------------------------------------------------

/** Starts the sign-in for a profile; the answer is the code to show. States arrive by onConnectState. */
export function startConnect(profileId: string): Promise<DeviceCodeInfo> {
  return call<DeviceCodeInfo>("startConnect", { profileId });
}

/** Stops waiting for the viewer to sign in. Never throws. */
export async function cancelConnect(): Promise<void> {
  try {
    await call<void>("cancelConnect");
  } catch {
    /* nothing to cancel */
  }
}

/** Is this profile connected? false anywhere the plugin is not. */
export async function isConnected(profileId: string): Promise<boolean> {
  try {
    return (await call<{ connected: boolean }>("status", { profileId })).connected === true;
  } catch {
    return false;
  }
}

/** A short-lived access token. Rejects with code "disconnected" (access removed) or "offline". */
export function getAccessToken(profileId: string): Promise<{ accessToken: string; expiresAt: number }> {
  return call<{ accessToken: string; expiresAt: number }>("accessToken", { profileId });
}

/** Revokes at Google (best effort) and forgets the sign-in. Resolves either way. */
export async function disconnect(profileId: string): Promise<void> {
  try {
    await call<void>("disconnect", { profileId });
  } catch {
    /* the plugin is not here: there is nothing to disconnect */
  }
  for (const listener of [...disconnectListeners]) listener(profileId);
  notifyChanged(profileId);
}

const disconnectListeners = new Set<(profileId: string) => void>();

/** Called after a profile is disconnected (so its cached subscriptions are deleted, not kept). */
export function onDisconnected(callback: (profileId: string) => void): () => void {
  disconnectListeners.add(callback);
  return () => void disconnectListeners.delete(callback);
}

/** Listens to the sign-in's progress for any profile. Returns the way to stop. */
export function onConnectState(callback: (data: { profileId: string; state: ConnectState }) => void): () => void {
  const cap = capacitor();
  if (!cap || typeof cap.addListener !== "function") return () => undefined;
  const handle = cap.addListener(PLUGIN, "connectState", ((data: { profileId: string; state: ConnectState }) => {
    callback(data);
    if (data.state === "connected") notifyChanged(data.profileId);
  }) as (data: never) => void);
  return () => void handle.remove();
}

// --- Who is connected, for the screens ------------------------------------------------------

const changeListeners = new Set<(profileId: string) => void>();

function notifyChanged(profileId: string): void {
  for (const listener of [...changeListeners]) listener(profileId);
}

/**
 * Whether sign-in is possible here and whether this profile is connected. `null` while it is
 * being learned, so a screen can wait instead of flashing the wrong state. Switching profile
 * answers for the other profile at once.
 */
export function useYoutubeConnection(profileId: string | undefined): { available: boolean | null; connected: boolean | null } {
  const [available, setAvailable] = useState<boolean | null>(null);
  const [connected, setConnected] = useState<{ profileId: string; value: boolean } | null>(null);

  useEffect(() => {
    let live = true;
    void connectAvailable().then((value) => live && setAvailable(value));
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    if (!profileId) return;
    let live = true;
    const refresh = () => void isConnected(profileId).then((value) => live && setConnected({ profileId, value }));
    refresh();
    const listener = (changed: string) => {
      if (changed === profileId) refresh();
    };
    changeListeners.add(listener);
    return () => {
      live = false;
      changeListeners.delete(listener);
    };
  }, [profileId]);

  return {
    available,
    connected: connected && connected.profileId === profileId ? connected.value : null,
  };
}
